import React, { useEffect, useRef } from 'react';

const GSI_READY_EVENT = 'google-gsi-ready';
let gsiInitialized = false;

const GoogleSignIn = ({
  onSuccess,
  onError,
  size = 'large',
  text = 'signin_with',
  width = 280,
  theme = 'outline',
  render = true,
  onReady,
  className = '',
  containerStyle,
}) => {
  const buttonRef = useRef(null);
  const renderedRef = useRef(false);

  const handleCredentialResponse = React.useCallback((response) => {
    try {
      const base64Url = response.credential.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(window.atob(base64));
      const userInfo = {
        email: payload.email,
        name: payload.name,
        given_name: payload.given_name,
        family_name: payload.family_name,
        picture: payload.picture,
        sub: payload.sub,
        credential: response.credential,
      };
      onSuccess?.(userInfo);
    } catch (error) {
      console.error('Error processing Google credential:', error);
      onError?.(error);
    }
  }, [onSuccess, onError]);

  const renderMyButton = React.useCallback(() => {
    if (!render || !buttonRef.current || !window.google?.accounts?.id || renderedRef.current) return;
    try {
      window.google.accounts.id.renderButton(buttonRef.current, { theme, size, text, width });
      renderedRef.current = true;
    } catch (err) {
      console.error('Google Sign-In render error:', err);
    }
  }, [render, theme, size, text, width]);

  useEffect(() => {
    const doInit = () => {
      if (!window.google?.accounts?.id || gsiInitialized) return;
      gsiInitialized = true;
      try {
        window.google.accounts.id.initialize({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
          callback: handleCredentialResponse,
        });
      } catch (err) {
        console.error('Google Sign-In initialize error:', err);
        gsiInitialized = false;
      }
    };

    const initAndDispatch = () => {
      doInit();
      window.dispatchEvent(new Event(GSI_READY_EVENT));
    };

    if (document.getElementById('google-gsi-script')) {
      if (window.google?.accounts?.id) {
        doInit();
        renderMyButton();
      }
      window.addEventListener(GSI_READY_EVENT, renderMyButton);
    } else {
      const script = document.createElement('script');
      script.id = 'google-gsi-script';
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = initAndDispatch;
      document.head.appendChild(script);
      window.addEventListener(GSI_READY_EVENT, renderMyButton);
    }

    return () => window.removeEventListener(GSI_READY_EVENT, renderMyButton);
  }, [renderMyButton, handleCredentialResponse]);

  useEffect(() => {
    if (window.google?.accounts?.id && onReady) {
      onReady(() => window.google?.accounts?.id?.prompt());
    }
  }, [onReady]);

  if (!render) return null;

  return (
    <div 
      ref={buttonRef}
      className={className}
      style={{
        display: 'flex', 
        justifyContent: 'center',
        minHeight: '44px',
        ...containerStyle,
      }}
    />
  );
};

export default GoogleSignIn;
